/**
 * MacroItemTile — first-class layout item rendering a single macro from
 * the library. Tap to arm at next downbeat; tap again to kill (snaps to
 * center). Visual state follows the bridge's `macro.state` broadcasts —
 * never set locally.
 *
 * Differs from the old dock-based `MacroButton` only in sizing: this
 * tile fills its EditableItem container (item.w × item.h) instead of
 * the dock's flex constraints. The data-state styling, progress strip,
 * and runtime subscription stay identical.
 *
 * If the referenced macro is missing from the library (e.g. user
 * deleted it but kept the tile), the face shows a dim "macro missing"
 * affordance and tap is disabled. Opening the ⋯ editor lets the user
 * delete the orphan tile.
 */

import { createEffect, createMemo, createSignal, on, type Accessor } from 'solid-js';
import { macros } from '../macros/store.ts';
import { macroStates } from '../macros/runtime.ts';
import type { Macro } from '@opbuttons/schema';
import { anyBridgeConnectedReactive } from '../opbridge/store.ts';
import { pushMacro, armMacroAction, fanoutTargets } from '../opbridge/dispatch.ts';
import { pokeEnginesStatePoll } from '../opbridge/enginesStatePoll.ts';
import type { MacroItem, LayoutColor } from '../layout/store.ts';

interface MacroItemTileProps {
  item: MacroItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

export function MacroItemTile(props: MacroItemTileProps) {
  const macro = createMemo(() => macros().find((m) => m.id === props.item.macroId) ?? null);
  const snapshot = createMemo(() => {
    const m = macro();
    return m ? macroStates()[m.id] : undefined;
  });
  // Optimistic overlay so the tile reacts on tap (the engine-state poll is
  // authoritative but arrives ~150ms-1s later). Cleared once a real
  // snapshot state lands for this macro.
  const [optimistic, setOptimistic] =
    createSignal<'idle' | 'armed' | 'running' | 'killing' | null>(null);
  const state = createMemo(() => optimistic() ?? snapshot()?.state ?? 'idle');
  const progress = createMemo(() => snapshot()?.laneProgress[0] ?? 0);
  createEffect(on(
    () => snapshot()?.state,
    (real) => { if (real) setOptimistic(null); },
    { defer: true },
  ));

  // Affordance text — lanes × bars, same convention as the old MacroButton.
  const subtitle = createMemo(() => {
    const m = macro();
    if (!m) return 'missing';
    const lanes = m.lanes.length;
    const bars = m.triggerLengthBeats / 4;
    return `${lanes} lane${lanes === 1 ? '' : 's'} · ${bars} bar${bars === 1 ? '' : 's'}`;
  });

  // Face options: user-override name + hide toggles for label/name.
  const displayName = () => props.item.name ?? macro()?.name ?? 'macro missing';
  const showName = () => !props.item.hideName;
  const showLabel = () => !props.item.hideLabel;

  function tap() {
    const m = macro();
    if (!m) return;
    if (!anyBridgeConnectedReactive(props.item.bridgeIds)) return;
    const cur = state();
    setOptimistic(cur === 'idle' || cur === 'killing' ? 'armed' : 'killing');
    void driveArm(m);
  }

  /** Push the macro to every configured bridge's engine (idempotent) then
   *  toggle-arm it there (multi-select fan-out, 2026-08-13 — undefined
   *  bridgeIds resolves to the default bridge). Each target's push+arm
   *  runs in order against that target; targets run in parallel with each
   *  other. (Legacy Node-bridge `bridge.armMacro` removed — bucket B
   *  migration to OPBridge, DEC-464 unblocked the library-push scope.) */
  async function driveArm(m: Macro) {
    const perTarget = fanoutTargets(props.item.bridgeIds).map(async (target) => {
      const pushed = await pushMacro(m, target);
      const pushErr = pushed.find((r) => r.kind !== 'ok');
      if (pushErr) {
        console.warn(`[MacroItemTile] pushMacro → ${pushErr.target}: ${pushErr.kind}${pushErr.detail ? ' - ' + pushErr.detail : ''}`);
        return false;
      }
      const armed = await armMacroAction(m.id, target);
      const armErr = armed.find((r) => r.kind !== 'ok');
      if (armErr) {
        console.warn(`[MacroItemTile] armMacro → ${armErr.target}: ${armErr.kind}${armErr.detail ? ' - ' + armErr.detail : ''}`);
        return false;
      }
      return true;
    });
    const results = await Promise.all(perTarget);
    if (!results.some(Boolean)) { setOptimistic(null); return; }
    pokeEnginesStatePoll();
  }

  return (
    <button
      class="macro-item"
      type="button"
      data-state={state()}
      data-color={COLOR_TOKEN[props.item.color]}
      data-missing={macro() ? 'false' : 'true'}
      onPointerDown={tap}
      disabled={!anyBridgeConnectedReactive(props.item.bridgeIds) || !macro()}
    >
      {showName() && <div class="macro-item-name">{displayName()}</div>}
      {showLabel() && <div class="macro-item-sub">{subtitle()}</div>}
      <div class="macro-item-progress" style={{ '--p': progress() }} />
    </button>
  );
}
